import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { VStack, HStack, Section } from 'tio-ui/components/layout';
import Button from 'tio-reimagined/components/button';
import Select from 'tio-reimagined/components/select';
import Table from 'tio-reimagined/components/table';
import NativeChart from 'tio-reimagined/components/chart';
import type StoreService from '@ember-data/store';
import { not, eq } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import type { SortDirection } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import {
  getModelTypeFromDataset,
  getCompanyFilterField,
  AVAILABLE_DATASETS,
  TIME_RANGE_OPTIONS,
} from '../utils/dataset-utils';
import { ChartDataTransformer } from '../utils/chart-data-transformer';
import { OpenSearchFieldMapper } from '../utils/opensearch-field-mapper';
import { DateGroupingUtility } from '../utils/date-grouping';
import type { ChartDataPoint, ChartType } from 'tio-common/types/chart';
import type { FieldOption } from '../utils/opensearch-field-mapper';
import type { DateGroupingInterval } from '../utils/date-grouping';
import type Report from '../models/reports';
import { ReportLoader, type ReportLoadContext } from '../utils/report-loader';
import type {
  SearchAdapter,
  SearchFieldsResponse,
  SearchFieldPayload,
} from 'tio-common/models/search';
import { getDisplayGroup as sharedGetDisplayGroup } from '../utils/label-generation';

/**
 * Aggregation sort order options for chart data.
 * Controls how aggregation buckets are ordered in the OpenSearch query.
 */
export type AggregationSortOrder =
  | 'count-desc' // Most frequent first (default)
  | 'count-asc' // Least frequent first
  | 'alpha-asc' // Alphabetical A→Z
  | 'alpha-desc'; // Alphabetical Z→A

export const AGGREGATION_SORT_OPTIONS = [
  { key: 'count-desc', label: 'Count (High → Low)' },
  { key: 'count-asc', label: 'Count (Low → High)' },
  { key: 'alpha-asc', label: 'Alphabetical (A → Z)' },
  { key: 'alpha-desc', label: 'Alphabetical (Z → A)' },
];

interface ChartingSignature {
  Args: {
    // Data input
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openSearchResponse?: any; // OpenSearchResponse data to display
    selectedField: string;
    chartType: ChartType;
    dateGrouping: DateGroupingInterval;
    showDataTable: boolean;
    selectedDataset: string;
    selectedTimeRange: string;
    sumField?: string; // Optional field to sum instead of counting
    summaryTitle?: string; // Optional title for summary charts
    sortOrder?: AggregationSortOrder; // Optional sort order for aggregation buckets
    companyId?: string;
    showIndividualApplications?: boolean;

    // Event handlers - all required for proper parent/child communication
    onFieldChange: (field: string) => void;
    onChartTypeChange: (type: ChartType) => void;
    onDateGroupingChange: (grouping: DateGroupingInterval) => void;
    onShowDataTableChange: (show: boolean) => void;
    onDatasetChange: (dataset: string) => void;
    onTimeRangeChange: (range: string) => void;
    onSummaryTitleChange?: (title: string) => void;
    onSortOrderChange?: (order: AggregationSortOrder) => void;

    // State
    isLoading?: boolean;
  };
}

export default class ChartingComponent extends Component<ChartingSignature> {
  @service declare store: StoreService;

  // Cache to prevent duplicate queries
  private lastQueryKey = '';
  private lastQueryResult: ChartDataPoint[] = [];

  @tracked isExporting = false;

  // Saved report functionality
  @tracked currentSavedReport?: Report;

  // Only keep getters that add logic beyond simple arg access

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(owner: any, args: ChartingSignature['Args']) {
    super(owner, args);
  }

  chartTypes = [
    { key: 'bar', label: 'Bar Chart' },
    { key: 'horizontalBar', label: 'Horizontal Bar' },
    { key: 'line', label: 'Line Chart' },
    { key: 'pie', label: 'Pie Chart' },
    { key: 'donut', label: 'Donut Chart' },
    { key: 'area', label: 'Area Chart' },
    { key: 'summary', label: 'Summary' },
  ];

  datasetOptions = AVAILABLE_DATASETS.map((dataset) => ({
    key: dataset.key,
    label: dataset.label,
  }));

  timeRangeOptions = TIME_RANGE_OPTIONS;

  // Use date grouping utility for options
  dateGroupingOptions = DateGroupingUtility.getAvailableIntervals();

  sortOrderOptions = AGGREGATION_SORT_OPTIONS;

  /**
   * Fetch fields for the current dataset using the /search/fields API.
   * This returns capability-aware field metadata (isGroupable, isSortable, etc.)
   * instead of raw OpenSearch mapping data.
   */
  fieldsData = trackedFunction(this, async (): Promise<FieldOption[]> => {
    // Capture values before await to avoid this context issues
    const selectedDataset = this.args.selectedDataset;

    const searchAdapter = this.store?.adapterFor?.('search') as SearchAdapter | undefined;

    if (!searchAdapter?.fields) {
      // Fallback to mapping-based approach if fields API not available
      return this.fetchFieldsFromMapping();
    }

    try {
      const modelType = this.getModelType();
      const fieldsResponse: SearchFieldsResponse = await searchAdapter.fields(modelType);

      const parsedFields = this.buildFieldOptionsFromFieldsResponse(fieldsResponse);

      if (parsedFields.length === 0) {
        throw new Error(`No fields found for ${modelType}`);
      }

      return parsedFields;
    } catch (error) {
      console.error('Error fetching fields:', error);

      // Fallback to mapping-based approach
      try {
        return await this.fetchFieldsFromMapping();
      } catch {
        return OpenSearchFieldMapper.getFallbackFields(selectedDataset);
      }
    }
  });

  /**
   * Fallback: fetch fields from raw OpenSearch mapping when /search/fields is unavailable
   */
  private async fetchFieldsFromMapping(): Promise<FieldOption[]> {
    const modelType = this.getModelType();
    const searchAdapter = this.store?.adapterFor?.('search') as SearchAdapter | undefined;
    const mapping = await searchAdapter?.mapping?.(modelType);

    if (mapping) {
      return this.parseFieldsFromMapping(mapping, modelType);
    }

    return OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset);
  }

  /**
   * Parse /search/fields response into FieldOption objects with full capabilities.
   */
  private buildFieldOptionsFromFieldsResponse(response: SearchFieldsResponse): FieldOption[] {
    const fields = response.fields || [];
    const modelType = this.getModelType();

    const parsedFields = fields
      .filter((field: SearchFieldPayload) => Boolean(field.name))
      // Filter out nested object subfields (e.g. employees_nested.employee_name_keyword)
      // that can't render as flat table columns. Keep custom field dot-paths.
      .filter((field: SearchFieldPayload) => {
        if (!field.name.includes('.')) return true;
        const parent = field.name.split('.')[0] || '';
        return parent.includes('custom_fields') || parent.includes('ef_fields');
      })
      .map((field: SearchFieldPayload) => {
        const name = field.name;
        const type = field.type || 'keyword';
        const capabilities = field.capabilities;
        const uiHints = field.uiHints;
        const isDate = type === 'date';

        return {
          key: name,
          label: field.label || OpenSearchFieldMapper.formatFieldLabel(name),
          type,
          group: sharedGetDisplayGroup(name, modelType, field.isCustom),
          isDate,
          isTimestamp: !isDate && OpenSearchFieldMapper.isTimestampField(name),
          adminOnly: OpenSearchFieldMapper.isAdminOnlyField(name),
          isFilterable: capabilities?.filter,
          isSortable: capabilities?.sort,
          isGroupable: capabilities?.aggregate,
          isRangeQueryable: capabilities?.range,
          isFullTextSearchable: capabilities?.fullText,
          isCustomField: field.isCustom,
          semanticRole: field.semanticRole,
          reportDatatype: field.reportDatatype ?? type,
          uiHints: uiHints
            ? {
                filterWidget: uiHints.filterWidget,
                multiSelect: uiHints.multiSelect,
              }
            : undefined,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // Deduplicate identical labels
    const uniqueFieldsMap = new Map<string, (typeof parsedFields)[0]>();
    for (const field of parsedFields) {
      if (!uniqueFieldsMap.has(field.label)) {
        uniqueFieldsMap.set(field.label, field);
      }
    }

    return Array.from(uniqueFieldsMap.values());
  }

  getFallbackFields(): FieldOption[] {
    return OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset);
  }

  get isMappingLoading() {
    return this.fieldsData.isLoading;
  }

  get currentFieldInfo() {
    return this.availableFields.find((field) => field.key === this.normalizedSelectedField);
  }

  get isCurrentFieldDate() {
    return this.currentFieldInfo?.isDate || false;
  }

  get isCurrentFieldTimestamp() {
    return this.currentFieldInfo?.isTimestamp || false;
  }

  // Parse fields from OpenSearch mapping using the utility (fallback path)
  parseFieldsFromMapping(
    mapping: Record<
      string,
      {
        mappings: {
          properties: Record<
            string,
            {
              type: string;
              fields?: Record<string, { type: string }>;
              properties?: Record<string, unknown>;
            }
          >;
        };
      }
    >,
    modelType: string
  ): FieldOption[] {
    const fields = OpenSearchFieldMapper.parseMapping(mapping);

    if (fields.length === 0) {
      console.warn('No mapping properties found for', modelType);
      return OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset);
    }

    return fields;
  }

  /**
   * All fields returned by the fields API (or mapping fallback)
   */
  get allFields() {
    return (
      this.fieldsData.value || OpenSearchFieldMapper.getFallbackFields(this.args.selectedDataset)
    );
  }

  /**
   * Fields available for the Group By dropdown.
   * Only includes fields that are groupable (aggregate capability) when using the fields API.
   * Falls back to all aggregatable fields when using the mapping API.
   */
  get availableFields() {
    const fields = this.allFields;

    // If fields have capability flags (from /search/fields API), filter to groupable only
    const hasCapabilities = fields.some(
      (f) => f.isGroupable !== undefined || f.isFilterable !== undefined
    );

    if (hasCapabilities) {
      return fields.filter((f) => f.isGroupable);
    }

    // Mapping fallback: all fields returned by parseMapping are already aggregatable
    return fields;
  }

  /**
   * The selected field key for the Group By dropdown.
   *
   * Field keys are camelCase (FE standard) — matches the fields API response.
   * buildQuery() handles camelCase → snake_case conversion at the OS boundary.
   */
  get normalizedSelectedField(): string | undefined {
    return this.args.selectedField || undefined;
  }

  chartData = trackedFunction(this, async (): Promise<ChartDataPoint[]> => {
    // Capture all the values we need BEFORE any await to avoid this context issues
    const selectedDataset = this.args.selectedDataset;
    const selectedGroupByField = this.args.selectedField;
    const selectedTimeRange = this.args.selectedTimeRange;
    const selectedDateGrouping = this.args.dateGrouping;
    const isCurrentFieldDate = this.isCurrentFieldDate;
    const isCurrentFieldTimestamp = this.isCurrentFieldTimestamp;
    const sortOrder = this.args.sortOrder || 'count-desc';

    // Create a unique key for this query to prevent duplicates
    const queryKey = `${selectedDataset}-${selectedGroupByField}-${selectedTimeRange}-${selectedDateGrouping}-${isCurrentFieldDate}-${isCurrentFieldTimestamp}-${sortOrder}`;

    // Check if we already have this exact query result
    if (this.lastQueryKey === queryKey && this.lastQueryResult.length > 0) {
      return this.lastQueryResult;
    }

    try {
      const modelType = this.getModelType();
      const builder = this.buildQuery();

      const query = {
        'model-type': modelType,
        'search-body': builder.build(),
      };

      const response = await this.store.adapterFor('search').search(query);
      const responseBuilder = OpenSearchResponseBuilder.from(response);

      const result = ChartDataTransformer.fromOpenSearchResponse(
        responseBuilder,
        selectedGroupByField,
        {
          isDate: isCurrentFieldDate,
          isTimestamp: isCurrentFieldTimestamp,
          dateGrouping: selectedDateGrouping,
          sumField: this.args.sumField,
        }
      );

      // Cache the result
      this.lastQueryKey = queryKey;
      this.lastQueryResult = result;

      return result;
    } catch (error) {
      console.error('Error fetching chart data:', error);
      return [];
    }
  });

  get currentData() {
    return this.chartData.value || [];
  }

  get totalCount() {
    return this.currentData.reduce((sum, dataPoint) => sum + dataPoint.value, 0);
  }

  get currentDataWithPercentages() {
    const total = this.totalCount;
    return this.currentData.map((dataPoint) => ({
      ...dataPoint,
      percentage: total > 0 ? ((dataPoint.value / total) * 100).toFixed(1) : '0.0',
    }));
  }

  get isLoading() {
    return this.chartData.isLoading;
  }

  getModelType = () => {
    if (
      this.args.selectedDataset === 'tas-program-instances' &&
      this.args.showIndividualApplications
    ) {
      return 'TasApplication';
    }

    return getModelTypeFromDataset(this.args.selectedDataset);
  };

  /**
   * Convert the sortOrder arg into an OpenSearch aggregation order clause.
   */
  getAggregationOrder(): Record<string, SortDirection> | undefined {
    switch (this.args.sortOrder) {
      case 'count-asc':
        return { _count: 'asc' };
      case 'alpha-asc':
        return { _key: 'asc' };
      case 'alpha-desc':
        return { _key: 'desc' };
      case 'count-desc':
      default:
        // count-desc is OpenSearch's default, no need to specify
        return undefined;
    }
  }

  buildQuery(): OpenSearchQueryBuilder {
    const builder = new OpenSearchQueryBuilder();
    builder.setHighlight([]);

    const companyId = this.args.companyId;
    if (companyId) {
      const filterField = getCompanyFilterField(this.args.selectedDataset);
      if (filterField) {
        builder.mustTerm(filterField, companyId);
      }
    }

    // Add time range filter if specified
    const timeFilter = this.getTimeRangeFilter();
    if (timeFilter) {
      builder.addFilter({
        range: {
          [timeFilter.field]: {
            gte: timeFilter.gte,
          },
        },
      });
    }

    const aggKey = `${this.args.selectedField}_agg`;
    const aggOrder = this.getAggregationOrder();

    // Add aggregations based on field type
    if (this.isCurrentFieldDate) {
      builder.addTermsAggregation(aggKey, this.args.selectedField, 100, aggOrder);
    } else {
      builder.addTermsAggregation(
        aggKey,
        this.args.selectedField,
        this.isCurrentFieldTimestamp ? 1000 : 100,
        aggOrder
      );
    }

    // If sumField is provided, add a nested sum aggregation
    if (this.args.sumField) {
      const sumAggKey = `${this.args.sumField}_sum`;
      builder.addNestedAggregation(aggKey, sumAggKey, {
        sum: {
          field: this.args.sumField,
        },
      });
    }

    return builder;
  }

  // Helper method for date grouping intervals - now using utility
  getCalendarInterval() {
    return DateGroupingUtility.getCalendarInterval(this.args.dateGrouping);
  }

  getDateFormat() {
    return DateGroupingUtility.getDateFormat(this.args.dateGrouping);
  }

  get chartXLabel() {
    if (this.args.chartType === 'summary' && this.args.summaryTitle) return undefined;
    return this.currentFieldInfo?.label ?? 'Category';
  }

  get chartYLabel() {
    return this.args.sumField ? 'Amount' : 'Count';
  }

  get chartLabel() {
    if (this.args.chartType === 'summary') {
      return this.args.summaryTitle || 'Total';
    }
    return undefined;
  }

  updateChartType = (key: string) => {
    if (key) {
      this.args.onChartTypeChange(key as ChartType);
    }
  };

  updateDataset = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onDatasetChange(key);
    }
  };

  updateTimeRange = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onTimeRangeChange(key);
    }
  };

  updateGroupByField = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onFieldChange(key);
    }
  };

  updateSummaryTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.onSummaryTitleChange?.(target.value);
  };

  updateDateGrouping = (key: string) => {
    if (key) {
      this.clearQueryCache();
      // Notify parent component of the change
      this.args.onDateGroupingChange(key as DateGroupingInterval);
    }
  };

  updateSortOrder = (key: string) => {
    if (key) {
      this.clearQueryCache();
      this.args.onSortOrderChange?.(key as AggregationSortOrder);
    }
  };

  toggleTableView = () => {
    // Notify parent component of the change
    this.args.onShowDataTableChange(!this.args.showDataTable);
  };

  clearQueryCache() {
    this.lastQueryKey = '';
    this.lastQueryResult = [];
  }

  getDefaultGroupByField() {
    return OpenSearchFieldMapper.getDefaultField(this.args.selectedDataset);
  }

  // Helper method to get the date field name for time range filtering
  getDateFieldForDataset() {
    switch (this.args.selectedDataset) {
      case 'people':
        return 'updated_at'; // People records use updated_at
      case 'companies':
        return 'updated_at'; // Company records use updated_at
      case 'tas-program-instances':
        return 'created_at'; // TAS programs use created_at
      case 'tas-program-templates':
        return 'created_at'; // Program templates use created_at
      case 'pslf-forms':
        return 'created_at'; // PSLF forms use created_at
      default:
        return 'created_at';
    }
  }

  // Helper method to convert time range to date filter
  getTimeRangeFilter(): { field: string; gte: string } | null {
    // Don't apply time range filter if:
    // 1. No time range selected or "all" is selected
    // 2. We're grouping by a date field (date grouping handles the time aspect)
    if (
      !this.args.selectedTimeRange ||
      this.args.selectedTimeRange === 'all' ||
      this.isCurrentFieldDate
    ) {
      return null;
    }

    const dateField = this.getDateFieldForDataset();
    const now = new Date();
    let startDate: Date;

    // Parse the time range format (e.g., "3m" = 3 months)
    const match = this.args.selectedTimeRange.match(/^(\d+)([myd])$/);
    if (!match || !match[1] || !match[2]) return null;

    const [, amount, unit] = match;
    const amountNum = parseInt(amount, 10);

    switch (unit) {
      case 'm': // months
        startDate = new Date(now.getFullYear(), now.getMonth() - amountNum, now.getDate());
        break;
      case 'y': // years
        startDate = new Date(now.getFullYear() - amountNum, now.getMonth(), now.getDate());
        break;
      case 'd': // days
        startDate = new Date(now.getTime() - amountNum * 24 * 60 * 60 * 1000);
        break;
      default:
        return null;
    }

    return {
      field: dateField,
      gte: startDate.toISOString(),
    };
  }

  exportData = async () => {
    if (this.isExporting) {
      return; // Prevent double-clicks
    }

    this.isExporting = true;

    try {
      // Build the same query we use for charting but for CSV export
      const modelType = this.getModelType();

      // For CSV export, we want the raw data, not aggregated data
      const builder = new OpenSearchQueryBuilder();
      builder.setHighlight([]);
      // Don't set size to 0 for CSV - we want actual records

      const query = {
        'model-type': modelType,
        'search-body': builder.build(),
      };

      // Use the search adapter's downloadCsv method
      await this.store.adapterFor('search').downloadCsv(query);
    } catch (_error) {
      // Could add a toast notification here
    } finally {
      this.isExporting = false;
    }
  };

  // Saved search methods - ReportToolbar handles the UI, these handle the logic

  handleSaveReport = (report: Report) => {
    this.currentSavedReport = report;
  };

  handleLoadReport = (report: Report) => {
    // Use the ReportLoader for intelligent report loading
    const context: ReportLoadContext = {
      setDataset: (dataset: string) => {
        this.args.onDatasetChange(dataset);
      },
      setGroupByField: (field: string) => {
        this.args.onFieldChange(field);
      },
      setChartType: (type: ChartType) => {
        this.args.onChartTypeChange(type);
      },
      setTimeRange: (range: string) => {
        this.args.onTimeRangeChange(range);
      },
      setDateGrouping: (grouping: DateGroupingInterval) => {
        this.args.onDateGroupingChange(grouping);
      },
      setShowTableView: (show: boolean) => {
        this.args.onShowDataTableChange(show);
      },
      setSortOrder: (order: string) => {
        this.args.onSortOrderChange?.(order as AggregationSortOrder);
      },
      clearQueryCache: this.clearQueryCache,
      setCurrentReport: this.setCurrentReport,
    };

    ReportLoader.loadReport(report, context).catch((error) => {
      console.error('Error loading report:', error);
    });
  };

  handleDeleteReport = (report: Report) => {
    // Clear current report if it was deleted
    if (this.currentSavedReport?.id === report.id) {
      this.currentSavedReport = undefined;
    }
  };

  // Method to set current report (used by ReportToolbar)
  setCurrentReport = (report: Report) => {
    this.currentSavedReport = report;
  };

  get currentSearchConfig() {
    // Build the OpenSearch query
    const query = this.buildQuery().build();

    // Create URL search params for the current state
    const urlParams = new URLSearchParams();
    urlParams.set('dataset', this.args.selectedDataset);
    urlParams.set('groupBy', this.args.selectedField);
    urlParams.set('chartType', this.args.chartType);
    urlParams.set('timeRange', this.args.selectedTimeRange);
    urlParams.set('dateGrouping', this.args.dateGrouping);
    if (this.args.sortOrder) {
      urlParams.set('sortOrder', this.args.sortOrder);
    }

    return {
      urlParams: urlParams.toString(),
      openSearchQuery: query,
      metadata: {
        dataset: this.args.selectedDataset,
        groupByField: this.args.selectedField,
        chartType: this.args.chartType,
        timeRange: this.args.selectedTimeRange,
        dateGrouping: this.args.dateGrouping,
        sortOrder: this.args.sortOrder || 'count-desc',
      },
    };
  }

  <template>
    <VStack>
      <Section>
        <:header>Chart Configuration</:header>
        <:body>

          {{#if this.currentSavedReport}}
            <div
              class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4 mb-6"
            >
              <div class="flex items-center">
                <svg
                  class="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                <div>
                  <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100">
                    Currently viewing:
                    {{this.currentSavedReport.displayName}}
                  </h4>
                  <p class="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Report details available
                  </p>
                </div>
              </div>
            </div>
          {{/if}}

          <HStack class="mb-6 gap-4">
            <div class="flex-1">
              <Select
                @label="Chart Type"
                @items={{this.chartTypes}}
                @selected={{@chartType}}
                @onChange={{this.updateChartType}}
              />
            </div>
            <div class="flex-1">
              <Select
                @label="Dataset"
                @items={{this.datasetOptions}}
                @selected={{@selectedDataset}}
                @onChange={{this.updateDataset}}
              />
            </div>
            <div class="flex-1">
              <Select
                @label="Group By Field"
                @items={{this.availableFields}}
                @selected={{this.normalizedSelectedField}}
                @onChange={{this.updateGroupByField}}
                @isLoading={{this.isMappingLoading}}
                @placeholder={{if this.isMappingLoading "Loading fields..." "Select field..."}}
              />
            </div>
            {{#if this.isCurrentFieldDate}}
              <div class="flex-1">
                <Select
                  @label="Date Grouping"
                  @items={{this.dateGroupingOptions}}
                  @selected={{@dateGrouping}}
                  @onChange={{this.updateDateGrouping}}
                />
              </div>
            {{else}}
              <div class="flex-1">
                <Select
                  @label="Time Range"
                  @items={{this.timeRangeOptions}}
                  @selected={{@selectedTimeRange}}
                  @onChange={{this.updateTimeRange}}
                />
              </div>
            {{/if}}
            <div class="flex-1">
              <Select
                @label="Sort Order"
                @items={{this.sortOrderOptions}}
                @selected={{if @sortOrder @sortOrder "count-desc"}}
                @onChange={{this.updateSortOrder}}
              />
            </div>
          </HStack>

          {{#if (eq @chartType "summary")}}
            <div class="mb-6">
              <label for="summary-title" class="block text-sm font-medium text-gray-700 mb-2">
                Summary Chart Title
              </label>
              <input
                id="summary-title"
                type="text"
                value={{@summaryTitle}}
                {{on "input" this.updateSummaryTitle}}
                placeholder="Enter chart title..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-900"
              />
            </div>
          {{/if}}

          {{#if this.isLoading}}
            <div class="flex items-center justify-center py-8">
              <div class="text-gray-500">Loading chart data...</div>
            </div>
          {{/if}}
        </:body>
      </Section>

      <Section>
        <:header>
          Chart Preview
        </:header>
        <:actions>
          <Button @intent="default" @appearance="outlined" {{on "click" this.toggleTableView}}>
            {{#if @showDataTable}}
              Hide Data Table
            {{else}}
              Show Data Table
            {{/if}}
          </Button>
        </:actions>
        <:body>
          {{#if this.isLoading}}
            <div
              class="w-full flex justify-center items-center min-h-96 border border-gray-200 rounded-lg"
            >
              <div class="text-gray-500">Loading chart...</div>
            </div>
          {{else if (not this.currentData.length)}}
            <div
              class="w-full flex justify-center items-center min-h-96 border border-gray-200 rounded-lg"
            >
              <div class="text-gray-500">No data available</div>
            </div>
          {{else}}
            <NativeChart
              @data={{this.currentData}}
              @type={{@chartType}}
              @xLabel={{this.chartXLabel}}
              @yLabel={{this.chartYLabel}}
              @label={{this.chartLabel}}
              @height={{384}}
              class="w-full min-h-96 rounded-lg"
            />

            {{#if @showDataTable}}
              <div class="mt-6">
                <h3 class="text-lg font-semibold mb-4 text-gray-900">Data Table</h3>
                {{#if this.isLoading}}
                  <div class="text-center py-4 text-gray-500">Loading table data...</div>
                {{else if (not this.currentData.length)}}
                  <div class="text-center py-4 text-gray-500">No data available</div>
                {{else}}
                  <div class="overflow-x-auto">
                    <Table @isLoading={{false}}>
                      <:thead as |options|>
                        <options.tr>
                          <options.th class="text-left">{{if
                              this.currentFieldInfo.label
                              this.currentFieldInfo.label
                              @selectedField
                            }}</options.th>
                          <options.th class="text-right">Count</options.th>
                          <options.th class="text-right">Percentage</options.th>
                        </options.tr>
                      </:thead>
                      <:tbody as |options|>
                        {{#each this.currentDataWithPercentages as |dataPoint|}}
                          <options.tr>
                            <options.td class="font-medium">{{dataPoint.category}}</options.td>
                            <options.td class="text-right">{{dataPoint.value}}</options.td>
                            <options.td class="text-right">{{dataPoint.percentage}}%</options.td>
                          </options.tr>
                        {{/each}}
                      </:tbody>
                    </Table>
                  </div>
                  <div class="mt-2 text-sm text-gray-600">
                    Total:
                    {{this.totalCount}}
                    records
                  </div>
                {{/if}}
              </div>
            {{/if}}
          {{/if}}
        </:body>
      </Section>

    </VStack>
  </template>
}
